/**
 * All color palettes used by the dynamic theme system.
 * Extracted from tailwind.config.js and standard Tailwind v3 palettes.
 * Used by useTheme to resolve theme color names to hex values.
 */

interface ColorPalette {
  [shade: string]: string
}

export const themeColorPalettes: Record<string, ColorPalette> = {
  // Custom brand palettes
  ovatugreen: {
    '50': '#E7F0DE',
    '100': '#DDEAD0',
    '200': '#C9DDB4',
    '300': '#B5D098',
    '400': '#A1C47C',
    '500': '#8CB760',
    '600': '#78A44A',
    '700': '#5C7D39',
    '800': '#3F5727',
    '900': '#233016',
    '950': '#151D0D',
  },
  ovatublue: {
    '50': '#f5f7fa',
    '100': '#eaedf4',
    '200': '#d0d9e7',
    '300': '#a7b9d2',
    '400': '#7894b8',
    '500': '#5776a0',
    '600': '#445e85',
    '700': '#384d6c',
    '800': '#31415b',
    '900': '#2c384c',
    '950': '#1e2633',
  },
  blush: {
    '50': '#fdf3f4',
    '100': '#fbe8e8',
    '200': '#f7d4d6',
    '300': '#f1b0b5',
    '400': '#e8848d',
    '500': '#da5162',
    '600': '#c63850',
    '700': '#a72942',
    '800': '#8c253d',
    '900': '#782339',
    '950': '#420f1b',
  },
  horizon: {
    '50': '#fff9eb',
    '100': '#fff3d1',
    '200': '#ffe1a1',
    '300': '#ffc966',
    '400': '#ffa628',
    '500': '#ff8900',
    '600': '#ff6b00',
    '700': '#d54e00',
    '800': '#b54204',
    '900': '#873507',
    '950': '#491701',
  },
  candy: {
    '50': '#fef1f5',
    '100': '#fee5ee',
    '200': '#ffcbdf',
    '300': '#ffa1c2',
    '400': '#ff81aa',
    '500': '#fa3a73',
    '600': '#ea184b',
    '700': '#cc0a35',
    '800': '#a80c2c',
    '900': '#8c0f28',
    '950': '#560112',
  },
  crush: {
    '50': '#FFFFFF',
    '100': '#FFFFFF',
    '200': '#FFFFFF',
    '300': '#FFFFFF',
    '400': '#FFFFFF',
    '500': '#FFFBE2',
    '600': '#FFF3AA',
    '700': '#FFEC72',
    '800': '#FFE43A',
    '900': '#FFDC02',
    '950': '#E5C500',
  },
  mist: {
    '50': '#f7f8f8',
    '100': '#eeeef0',
    '200': '#d8dadf',
    '300': '#b7bbc2',
    '400': '#8f95a1',
    '500': '#686e7a',
    '600': '#5c616d',
    '700': '#4b4f59',
    '800': '#40444c',
    '900': '#393b41',
    '950': '#26272b',
  },
  hot: {
    '50': '#fff1f2',
    '100': '#ffe4e6',
    '200': '#fecdd3',
    '300': '#fca5af',
    '400': '#fa7285',
    '500': '#f3405e',
    '600': '#e01e48',
    '700': '#bd133c',
    '800': '#9f1339',
    '900': '#871436',
    '950': '#4c0519',
  },
  sun: {
    '50': '#fef6ee',
    '100': '#fcebd8',
    '200': '#f8d2b0',
    '300': '#f3b27e',
    '400': '#ee8849',
    '500': '#e96825',
    '600': '#da501c',
    '700': '#b53b19',
    '800': '#90311c',
    '900': '#752b19',
    '950': '#3f120b',
  },
  surf: {
    '50': '#eefdfd',
    '100': '#d4f9f9',
    '200': '#aef1f3',
    '300': '#5de0e6',
    '400': '#37cfd9',
    '500': '#1bb3bf',
    '600': '#1a90a0',
    '700': '#1b7483',
    '800': '#1f5f6b',
    '900': '#1e4e5b',
    '950': '#0e333e',
  },
  navy: {
    '50': '#EBF2F9',
    '100': '#D7E5F4',
    '200': '#B0CBE8',
    '300': '#88B1DD',
    '400': '#6197D1',
    '500': '#397DC6',
    '600': '#2E649E',
    '700': '#224B77',
    '800': '#17324F',
    '900': '#0B1826',
    '950': '#060D14',
  },
  merlot: {
    '50': '#fdf3f3',
    '100': '#fbe5e5',
    '200': '#f8d0d0',
    '300': '#f1b0b0',
    '400': '#e78282',
    '500': '#da5959',
    '600': '#c53d3d',
    '700': '#a53030',
    '800': '#842929',
    '900': '#732929',
    '950': '#3e1111',
  },
  contrastPrimaryLight: {
    '50': '#f2f5ff',
    '100': '#d7e3ff',
    '200': '#bbc1ff',
    '300': '#9f9fff',
    '400': '#828dff',
    '500': '#666bff',
    '600': '#4d52e0',
    '700': '#3a3f99',
    '800': '#282c66',
    '900': '#161833',
    '950': '#0d101a',
  },
  contrastGrayLight: {
    '50': '#fafafa',
    '100': '#f5f5f5',
    '200': '#eeeeee',
    '300': '#e0e0e0',
    '400': '#bbbbbb',
    '500': '#999999',
    '600': '#777777',
    '700': '#555555',
    '800': '#333333',
    '900': '#222222',
    '950': '#111111',
  },
  contrastPrimaryDark: {
    '50': '#e2e2ff',
    '100': '#c4c4ff',
    '200': '#a6a6ff',
    '300': '#8888ff',
    '400': '#6a6aff',
    '500': '#4c4cff',
    '600': '#3a3ae0',
    '700': '#292999',
    '800': '#191966',
    '900': '#090933',
    '950': '#04041a',
  },
  contrastGrayDark: {
    '50': '#f0f0f0',
    '100': '#d9d9d9',
    '200': '#c2c2c2',
    '300': '#ababab',
    '400': '#949494',
    '500': '#7d7d7d',
    '600': '#666666',
    '700': '#4f4f4f',
    '800': '#383838',
    '900': '#212121',
    '950': '#0a0a0a',
  },

  // Standard Tailwind palettes (v3 hex values for theme consistency)
  zinc: {
    '50': '#fafafa',
    '100': '#f4f4f5',
    '200': '#e4e4e7',
    '300': '#d4d4d8',
    '400': '#a1a1aa',
    '500': '#71717a',
    '600': '#52525b',
    '700': '#3f3f46',
    '800': '#27272a',
    '900': '#18181b',
    '950': '#09090b',
  },
  slate: {
    '50': '#f8fafc',
    '100': '#f1f5f9',
    '200': '#e2e8f0',
    '300': '#cbd5e1',
    '400': '#94a3b8',
    '500': '#64748b',
    '600': '#475569',
    '700': '#334155',
    '800': '#1e293b',
    '900': '#0f172a',
    '950': '#020617',
  },
  stone: {
    '50': '#fafaf9',
    '100': '#f5f5f4',
    '200': '#e7e5e4',
    '300': '#d6d3d1',
    '400': '#a8a29e',
    '500': '#78716c',
    '600': '#57534e',
    '700': '#44403c',
    '800': '#292524',
    '900': '#1c1917',
    '950': '#0c0a09',
  },
  neutral: {
    '50': '#fafafa',
    '100': '#f5f5f5',
    '200': '#e5e5e5',
    '300': '#d4d4d4',
    '400': '#a3a3a3',
    '500': '#737373',
    '600': '#525252',
    '700': '#404040',
    '800': '#262626',
    '900': '#171717',
    '950': '#0a0a0a',
  },
  emerald: {
    '50': '#ecfdf5',
    '100': '#d1fae5',
    '200': '#a7f3d0',
    '300': '#6ee7b7',
    '400': '#34d399',
    '500': '#10b981',
    '600': '#059669',
    '700': '#047857',
    '800': '#065f46',
    '900': '#064e3b',
    '950': '#022c22',
  },
  fuchsia: {
    '50': '#fdf4ff',
    '100': '#fae8ff',
    '200': '#f5d0fe',
    '300': '#f0abfc',
    '400': '#e879f9',
    '500': '#d946ef',
    '600': '#c026d3',
    '700': '#a21caf',
    '800': '#86198f',
    '900': '#701a75',
    '950': '#4a044e',
  },
  blue: {
    '50': '#eff6ff',
    '100': '#dbeafe',
    '200': '#bfdbfe',
    '300': '#93c5fd',
    '400': '#60a5fa',
    '500': '#3b82f6',
    '600': '#2563eb',
    '700': '#1d4ed8',
    '800': '#1e40af',
    '900': '#1e3a8a',
    '950': '#172554',
  },
  sky: {
    '50': '#f0f9ff',
    '100': '#e0f2fe',
    '200': '#bae6fd',
    '300': '#7dd3fc',
    '400': '#38bdf8',
    '500': '#0ea5e9',
    '600': '#0284c7',
    '700': '#0369a1',
    '800': '#075985',
    '900': '#0c4a6e',
    '950': '#082f49',
  },
  pink: {
    '50': '#fdf2f8',
    '100': '#fce7f3',
    '200': '#fbcfe8',
    '300': '#f9a8d4',
    '400': '#f472b6',
    '500': '#ec4899',
    '600': '#db2777',
    '700': '#be185d',
    '800': '#9d174d',
    '900': '#831843',
    '950': '#500724',
  },
  orange: {
    '50': '#fff7ed',
    '100': '#ffedd5',
    '200': '#fed7aa',
    '300': '#fdba74',
    '400': '#fb923c',
    '500': '#f97316',
    '600': '#ea580c',
    '700': '#c2410c',
    '800': '#9a3412',
    '900': '#7c2d12',
    '950': '#431407',
  },
  green: {
    '50': '#f0fdf4',
    '100': '#dcfce7',
    '200': '#bbf7d0',
    '300': '#86efac',
    '400': '#4ade80',
    '500': '#22c55e',
    '600': '#16a34a',
    '700': '#15803d',
    '800': '#166534',
    '900': '#14532d',
    '950': '#052e16',
  },
  red: {
    '50': '#fef2f2',
    '100': '#fee2e2',
    '200': '#fecaca',
    '300': '#fca5a5',
    '400': '#f87171',
    '500': '#ef4444',
    '600': '#dc2626',
    '700': '#b91c1c',
    '800': '#991b1b',
    '900': '#7f1d1d',
    '950': '#450a0a',
  },
  amber: {
    '50': '#fffbeb',
    '100': '#fef3c7',
    '200': '#fde68a',
    '300': '#fcd34d',
    '400': '#fbbf24',
    '500': '#f59e0b',
    '600': '#d97706',
    '700': '#b45309',
    '800': '#92400e',
    '900': '#78350f',
    '950': '#451a03',
  },
  yellow: {
    '50': '#fefce8',
    '100': '#fef9c3',
    '200': '#fef08a',
    '300': '#fde047',
    '400': '#facc15',
    '500': '#eab308',
    '600': '#ca8a04',
    '700': '#a16207',
    '800': '#854d0e',
    '900': '#713f12',
    '950': '#422006',
  },
  rose: {
    '50': '#fff1f2',
    '100': '#ffe4e6',
    '200': '#fecdd3',
    '300': '#fda4af',
    '400': '#fb7185',
    '500': '#f43f5e',
    '600': '#e11d48',
    '700': '#be123c',
    '800': '#9f1239',
    '900': '#881337',
    '950': '#4c0519',
  },
}
